import React, { useMemo } from 'react';
import DiceCountSelector from './DiceCountSelector';
import { getButtonClasses } from '../../constants/styles';
import { CONTROL_BUTTON, LABEL_STYLES } from '../../constants/styles';
import { GameState } from '../../types';

interface WaitingRoomControlsProps {
  gameState: GameState;
  myPlayerId: string | null;
  dicePerPlayer: number;
  canAddAI: boolean;
  onDiceCountChange: (count: number) => void;
  onAddAIPlayer: () => void;
  onRemoveAIPlayer: () => void;
  onReadyUp: () => void;
}

const WaitingRoomControls: React.FC<WaitingRoomControlsProps> = ({
  gameState,
  myPlayerId,
  dicePerPlayer,
  canAddAI,
  onDiceCountChange,
  onAddAIPlayer,
  onRemoveAIPlayer,
  onReadyUp,
}) => {
  const aiPlayerCount = useMemo(() => {
    return gameState.players.filter(p => p.isAI).length;
  }, [gameState.players]);

  const myPlayer = useMemo(() => {
    return gameState.players.find(p => p.id === myPlayerId);
  }, [gameState.players, myPlayerId]);

  const isReady = myPlayer?.ready || false;
  const canRemoveAI = aiPlayerCount > 0;
  const MAX_AI_PLAYERS = 5; // 6 total - 1 human

  return (
    <div className="mt-2 p-2 bg-surface-50 dark:bg-gray-700 rounded border border-surface-200 dark:border-gray-600">
      {/* Dice and AI controls side-by-side */}
      <div className="flex items-center justify-center gap-6 mb-2">
        <DiceCountSelector
          dicePerPlayer={dicePerPlayer}
          onDiceCountChange={onDiceCountChange}
        />

        {/* AI Player controls */}
        <div className="flex items-center gap-2">
          <label className={`${LABEL_STYLES} flex-shrink-0`}>
            Bots:
          </label>
          <button
            onClick={onRemoveAIPlayer}
            disabled={!canRemoveAI}
            className={CONTROL_BUTTON}
            aria-label="Remove AI player"
          >
            −
          </button>
          <div className="w-10 h-8 bg-surface-50 dark:bg-gray-700 rounded flex items-center justify-center border border-surface-300 dark:border-gray-600">
            <span className="text-lg font-bold text-gray-900 dark:text-white">{aiPlayerCount}</span>
          </div>
          <button
            onClick={onAddAIPlayer}
            disabled={!canAddAI || aiPlayerCount >= MAX_AI_PLAYERS}
            className={CONTROL_BUTTON}
            aria-label="Add AI player"
          >
            +
          </button>
        </div>
      </div>

      {/* Ready button */}
      {!isReady ? (
        <button
          onClick={onReadyUp}
          className={`w-full ${getButtonClasses('primary', 'xs')}`}
        >
          Ready
        </button>
      ) : (
        <div className="text-center text-sm text-green-600 dark:text-green-400 font-semibold py-2">
          ✓ Ready! Waiting for other players...
        </div>
      )}
    </div>
  );
};

export default WaitingRoomControls;